'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import StructuredStepText from '@/components/common/StructuredStepText';
import WalkthroughControls from '@/components/common/WalkthroughControls';
import {
    buildStepText as buildStepExplanation,
    draw as drawBusStopCanvas,
    solve as solveLongDivision,
    type BusStopSolution,
} from '@/lib/solvers/busStop';

interface BusStopMethodProps {
    dividend: number;
    divisor: number;
    showText?: boolean;
    showImage?: boolean;
}

export default function BusStopMethod({
    dividend,
    divisor,
    showText = true,
    showImage = true,
}: BusStopMethodProps) {
    const stepCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
    const [copied, setCopied] = useState(false);
    const [downloaded, setDownloaded] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const solution = useMemo(() => {
        if (dividend === undefined || divisor === undefined) return null;
        return solveLongDivision(dividend, divisor);
    }, [dividend, divisor]);

    const totalSteps = solution && !('error' in solution) ? (solution.steps?.length ?? 0) : 0;

    useEffect(() => {
        setStepIndex(0);
    }, [totalSteps]);

    useEffect(() => {
        if (!showImage) return;
        if (!solution || 'error' in solution) return;
        if (typeof window === 'undefined') return;

        const sol = solution as BusStopSolution;

        stepCanvasRefs.current
            .filter((c): c is HTMLCanvasElement => c instanceof HTMLCanvasElement)
            .forEach((canvas) => {
                const n = Number(canvas.dataset.stepN);
                if (!Number.isFinite(n)) return;
                const upTo = Math.max(-1, Math.min(n - 1, (sol.steps?.length || 1) - 1));
                drawBusStopCanvas(canvas, sol, {
                    upToStep: upTo,
                    showSummary: upTo >= (sol.steps?.length || 1) - 1,
                });
            });
    }, [solution, showImage, stepIndex, totalSteps]);

    if (solution == null) return null;

    const isError = 'error' in solution;
    const text = buildStepExplanation(
        solution,
        totalSteps === 0 ? null : Math.min(stepIndex, totalSteps - 1),
    );

    async function copyText(): Promise<void> {
        try {
            await navigator.clipboard.writeText(text ?? '');
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch {}
    }

    function downloadPng(): void {
        const canvases = stepCanvasRefs.current.filter(
            (c): c is HTMLCanvasElement => c instanceof HTMLCanvasElement,
        );
        const c = canvases[canvases.length - 1];
        if (!c) return;
        const a = document.createElement('a');
        a.href = c.toDataURL('image/png');
        a.download = 'bus-stop-solution.png';
        a.click();
        setDownloaded(true);
        setTimeout(() => setDownloaded(false), 800);
    }

    return (
        <div className="w-full">
            {!isError && (
                <WalkthroughControls
                    stepIndex={stepIndex}
                    setStepIndex={setStepIndex}
                    totalSteps={totalSteps}
                />
            )}

            <div className="rounded-lg border-[0.5px] border-line bg-white p-6">
                {showText && (
                    <div>
                        <StructuredStepText
                            text={text}
                            renderStepExtras={(s) =>
                                showImage ? (
                                    <canvas
                                        data-step-n={s.n}
                                        ref={(el: HTMLCanvasElement | null) => {
                                            const idx = s.n - 1;
                                            stepCanvasRefs.current[idx] = el;
                                        }}
                                        aria-label={`Bus stop solution image for step ${s.n}`}
                                        className="mt-2 block max-w-full"
                                    />
                                ) : null
                            }
                        />
                        {!isError && (
                            <div className="mt-5 flex gap-2">
                                <button onClick={copyText} className="text-[13px]">
                                    {copied ? 'Copied!' : 'Copy text'}
                                </button>
                                {showImage && (
                                    <button onClick={downloadPng} className="text-[13px]">
                                        {downloaded ? 'Saved' : 'Download PNG'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
